/**
 * BubbleToolbar — toolbar flotante que aparece sobre la selección.
 *
 * Patrón Notion / CoAuthorix: en lugar de una toolbar fija arriba del
 * editor, mostramos los botones cuando el user selecciona texto. Es
 * menos invasivo y mantiene el foco en la prosa.
 *
 * Cuando el user clickea el botón "link", el toolbar se reemplaza por
 * un input inline (`LinkPopover`) sin dismissar.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  RemoveFormatting,
  Strikethrough,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  Type,
  Underline as UnderlineIcon,
} from 'lucide-react'
import type { Editor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'

import { LinkPopover } from './LinkPopover'

interface BubbleToolbarProps {
  editor: Editor
}

export function BubbleToolbar({ editor }: BubbleToolbarProps) {
  const { t } = useTranslation('editor')
  const [linkOpen, setLinkOpen] = useState(false)

  return (
    <BubbleMenu
      editor={editor}
      options={{ placement: 'top', offset: 8 }}
      shouldShow={({ editor: ed, from, to }) => {
        // Mostrar solo si hay selección de texto (no caret vacío) y el
        // editor está en modo edit.
        if (!ed.isEditable) return false
        if (from === to) return false
        // Evitar mostrar dentro de un code block (la prosa no aplica ahí).
        if (ed.isActive('codeBlock')) return false
        return true
      }}
      className="bg-surface border border-line rounded-md shadow-lg overflow-hidden"
    >
      {linkOpen ? (
        <LinkPopover editor={editor} onClose={() => setLinkOpen(false)} />
      ) : (
        <div className="flex items-center gap-0.5 px-1 py-1 flex-wrap max-w-[680px]">
          {/* ─── Tipo de bloque ──────────────────────────────────────── */}
          <BubbleBtn
            label={t('bubble.paragraph')}
            icon={Type}
            active={
              editor.isActive('paragraph') &&
              !editor.isActive('heading') &&
              !editor.isActive('blockquote') &&
              !editor.isActive('codeBlock')
            }
            onClick={() => editor.chain().focus().setParagraph().run()}
          />
          <BubbleBtn
            label={t('bubble.heading2')}
            icon={Heading2}
            active={editor.isActive('heading', { level: 2 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          />
          <BubbleBtn
            label={t('bubble.heading3')}
            icon={Heading3}
            active={editor.isActive('heading', { level: 3 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
          />
          <BubbleDivider />

          {/* ─── Marks inline ────────────────────────────────────────── */}
          <BubbleBtn
            label={t('bubble.bold')}
            icon={Bold}
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <BubbleBtn
            label={t('bubble.italic')}
            icon={Italic}
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <BubbleBtn
            label={t('bubble.underline')}
            icon={UnderlineIcon}
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />
          <BubbleBtn
            label={t('bubble.strike')}
            icon={Strikethrough}
            active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          />
          <BubbleBtn
            label={t('bubble.code')}
            icon={Code}
            active={editor.isActive('code')}
            onClick={() => editor.chain().focus().toggleCode().run()}
          />
          <BubbleBtn
            label={t('bubble.highlight')}
            icon={Highlighter}
            active={editor.isActive('highlight')}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
          />
          <BubbleDivider />

          {/* ─── Sub / Super ──────────────────────────────────────────── */}
          <BubbleBtn
            label={t('bubble.subscript')}
            icon={SubscriptIcon}
            active={editor.isActive('subscript')}
            onClick={() => editor.chain().focus().toggleSubscript().run()}
          />
          <BubbleBtn
            label={t('bubble.superscript')}
            icon={SuperscriptIcon}
            active={editor.isActive('superscript')}
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
          />
          <BubbleDivider />

          {/* ─── Link ─────────────────────────────────────────────────── */}
          <BubbleBtn
            label={t('bubble.link')}
            icon={LinkIcon}
            active={editor.isActive('link')}
            onClick={() => setLinkOpen(true)}
          />
          <BubbleDivider />

          {/* ─── Listas + cita ────────────────────────────────────────── */}
          <BubbleBtn
            label={t('bubble.bullet_list')}
            icon={List}
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
          <BubbleBtn
            label={t('bubble.ordered_list')}
            icon={ListOrdered}
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
          <BubbleBtn
            label={t('bubble.blockquote')}
            icon={Quote}
            active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          />
          <BubbleDivider />

          {/* ─── Alineación ───────────────────────────────────────────── */}
          <BubbleBtn
            label={t('bubble.align_left')}
            icon={AlignLeft}
            active={editor.isActive({ textAlign: 'left' })}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          />
          <BubbleBtn
            label={t('bubble.align_center')}
            icon={AlignCenter}
            active={editor.isActive({ textAlign: 'center' })}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          />
          <BubbleBtn
            label={t('bubble.align_right')}
            icon={AlignRight}
            active={editor.isActive({ textAlign: 'right' })}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          />
          <BubbleDivider />

          {/* ─── Limpiar formato ──────────────────────────────────────── */}
          <BubbleBtn
            label={t('bubble.clear_formatting')}
            icon={RemoveFormatting}
            active={false}
            onClick={() =>
              editor.chain().focus().unsetAllMarks().clearNodes().run()
            }
          />
        </div>
      )}
    </BubbleMenu>
  )
}

function BubbleBtn({
  label,
  icon: Icon,
  active,
  onClick,
}: {
  label: string
  icon: typeof Bold
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={
        active
          ? 'p-1.5 rounded-sm bg-brand-soft text-brand'
          : 'p-1.5 rounded-sm text-ink-muted hover:bg-surface-muted hover:text-ink'
      }
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  )
}

function BubbleDivider() {
  return <div className="w-px h-5 bg-line mx-0.5" aria-hidden />
}
